import React from 'react';
import { theme } from '../theme';
import { usePaletteMode } from '../context/PaletteModeContext';

// ─── NavyKpiCard ─────────────────────────────────────────
export const NavyKpiCard: React.FC<{
  // ReactNode (not just string), matching BentoCard's own title prop — a caller reading a served
  // bucket word wraps it in ServedFace, which is not a plain string while /config is still resolving.
  title: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
  // Used by StatistikSeite Eingegangen card to render the stats-mode swap toggle.
  headerAction?: React.ReactNode;
  // Gray-out the card surface (e.g. archive-mode treatment on StatistikSeite).
  muted?: boolean;
  // Inline style applied to the title span — used by StatistikSeite to cross-fade titles on mode swap.
  titleStyle?: React.CSSProperties;
  // The third row's child card (Statistik, 2026-10-28): the same surface a size down — title 11 px
  // in the on-dark ink, tighter padding — so a child reads as a part of the card above it.
  compact?: boolean;
}> = ({ title, children, style, headerAction, muted, titleStyle, compact }) => {
  const { mode: palette } = usePaletteMode();
  const arch = theme.colors.mode.archived[palette];
  return (
  <div data-card style={{
    // Muted = archived card surface — light/dark per palette preference, so title flips navy/white.
    // theme.colors.neutral stays defined (sentiment Neutral slice still uses it).
    backgroundColor: muted ? arch.card : theme.colors.navy,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.card,
    display: 'flex', flexDirection: 'column' as const,
    overflow: 'hidden', minWidth: 0,
    // Morph+ recolor — matches DistributionHero mode swap.
    transition: 'background-color 0.58s cubic-bezier(0.4,0,0.2,1)',
    ...style,
  }}>
    <div style={{
      display: 'flex', alignItems: 'center', flexShrink: 0,
      padding: compact ? `6px 10px 0` : `${theme.spacing.sm}px ${theme.spacing.md}px`,
      paddingBottom: compact ? 0 : headerAction ? theme.spacing.sm : theme.spacing.md,
    }}>
      <span style={{
        flex: 1, fontSize: compact ? 11 : 14, fontWeight: compact ? 500 : 600, fontFamily: theme.font.family,
        color: muted ? arch.text : compact ? theme.colors.textOnDark : theme.colors.white, userSelect: 'none',
        // Only the archive-lens child title carries an opacity: the Eingegangen card's nodes carry none
        // (kpiFlip pins that it never fades).
        ...(muted && compact ? { opacity: 0.8 } : {}),
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        ...titleStyle,
      }}>
        {title}
      </span>
      {headerAction && (
        <div style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}>
          {headerAction}
        </div>
      )}
    </div>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' as const, justifyContent: 'center', padding: compact ? `2px 10px 7px` : `${theme.spacing.sm}px ${theme.spacing.md}px` }}>{children}</div>
  </div>
  );
};
